import { MODE_CONSEQUENCES, MODE_LABELS } from '../../design/modeCopy';
import { DayMode } from '../../../domain/routine/enums';
import type { ModeDensity, ModeExigence, ModePresentationDefinition } from './presentation-types';

/**
 * ASCEND V7 — présentation des 9 modes de contexte, réconciliée avec `DayMode` du domaine.
 *
 * Le mode porte la LOGIQUE FONCTIONNELLE (combien de missions, priorité, exigence, densité) —
 * cette logique reste entièrement dans `domain/routine/DayModeRules.ts` et n'est jamais
 * recalculée ici. Ce fichier ne porte que la PRÉSENTATION : image, accent, icône, lumière,
 * ton, contenu éditorial. `label` et `functionalConsequence` référencent le texte déjà validé
 * de `presentation/design/modeCopy.ts` plutôt que de le retyper une seconde fois.
 *
 * Ton éditorial partagé par les 9 modes : c'est la valeur par défaut du système avant qu'un
 * état émotionnel ne prenne le dessus (voir `priority-matrix.js` du handoff, qui utilise cette
 * même chaîne comme repli quand aucune famille n'est active).
 */
const DEFAULT_MODE_TONE = 'sobre, adulte, sans effusion';

/**
 * Point focal `object-position` par image de hero — pack V2 (1870×841, ratio ~2.22:1), chaque
 * photo a une composition propre (sujet à droite, à gauche, centré...) ; un centrage uniforme
 * couperait le sujet réel d'une image sur deux, en particulier sur les recadrages mobiles plus
 * étroits que le ratio source. Valeurs déterminées par inspection visuelle directe de chaque
 * fichier V2 — voir revue visuelle « intégration finale des 20 visuels » de la mission V7.
 */
const HERO_IMAGE_POSITION: Record<string, string> = {
  normal: '50% 55%',
  charge: '75% 50%',
  voyage: '60% 45%',
  recuperation: '55% 50%',
  difficile: '65% 55%',
  elevation: '75% 55%',
  'moment-decisif': '72% 45%',
  imprevu: '75% 60%',
  transition: '35% 55%',
};
const DEFAULT_HERO_IMAGE_POSITION = '50% 50%';

function content(
  key: string,
  mantra: string,
  thought: string,
  intention: string,
  routines: readonly string[],
) {
  return {
    mantraKey: `ascend.mode.${key}.mantra`,
    mantra,
    thoughtKey: `ascend.mode.${key}.thought`,
    thought,
    intentionKey: `ascend.mode.${key}.intention`,
    intention,
    routineKeys: routines.map((_, index) => `ascend.mode.${key}.routine.${index}`),
    routines,
  };
}

function mode(
  domainMode: DayMode,
  key: string,
  icon: ModePresentationDefinition['icon'],
  accent: string,
  light: string,
  density: ModeDensity,
  exigence: ModeExigence,
  mantra: string,
  thought: string,
  intention: string,
  routines: readonly string[],
): ModePresentationDefinition {
  return {
    mode: domainMode,
    key,
    labelKey: `ascend.mode.${key}.label`,
    label: MODE_LABELS[domainMode],
    icon,
    scopeClass: `mode-${key}`,
    heroImageKey: `hero-${key}-v2.png`,
    heroImagePosition: HERO_IMAGE_POSITION[key] ?? DEFAULT_HERO_IMAGE_POSITION,
    accent,
    light,
    density,
    exigence,
    tone: DEFAULT_MODE_TONE,
    shortDescriptionKey: `ascend.mode.${key}.shortDescription`,
    shortDescription: intention,
    functionalConsequenceKey: `ascend.mode.${key}.functionalConsequence`,
    functionalConsequence: MODE_CONSEQUENCES[domainMode],
    content: content(key, mantra, thought, intention, routines),
  };
}

export const MODE_PRESENTATIONS = {
  [DayMode.normal]: mode(
    DayMode.normal,
    'normal',
    'normal',
    '#D4AF37',
    'douce et équilibrée',
    'standard',
    'standard',
    'Clarté. Discipline. Maîtrise.',
    '« La maîtrise commence par le choix de ce qui mérite ton énergie. »',
    'Tenir le cap ordinaire, sans le subir.',
    ['Commencer avec intention', "Réaliser l'action essentielle", 'Terminer avec clarté'],
  ),
  [DayMode.busy]: mode(
    DayMode.busy,
    'charge',
    'charge',
    '#B87333',
    'dirigée et contrastée',
    'dense',
    'haute',
    'Priorité. Concentration. Tenue.',
    "« Ce qui n'est pas essentiel n'a pas à entrer dans la journée. »",
    'Quand la charge monte, la clarté doit se resserrer.',
    ['Réduire au strict nécessaire', 'Protéger le bloc essentiel', 'Refuser les distractions inutiles'],
  ),
  [DayMode.travel]: mode(
    DayMode.travel,
    'voyage',
    'voyage',
    '#5C93A8',
    "ouverte sur l'horizon",
    'standard',
    'souple',
    'Souplesse. Repère. Continuité.',
    '« Les repères changent ; la direction demeure. »',
    'Rester aligné, même en mouvement.',
    ["Garder un point d'ancrage", 'Adapter sans abandonner', "Intégrer l'expérience du déplacement"],
  ),
  [DayMode.recovery]: mode(
    DayMode.recovery,
    'recuperation',
    'recuperation',
    '#6E8F6A',
    'diffuse et enveloppante',
    'aérée',
    'basse',
    'Souffle. Ralentir. Restaurer.',
    '« La récupération fait partie de la progression. »',
    'Réparer sans forcer.',
    ["Réduire l'intensité", 'Restaurer le corps', "Préparer le retour à l'équilibre"],
  ),
  [DayMode.difficult]: mode(
    DayMode.difficult,
    'difficile',
    'difficile',
    '#B0453F',
    'basse avec une ouverture discrète',
    'aérée',
    'minimale',
    'Tenue. Courage. Essentiel.',
    "« Une journée difficile n'exige pas une conduite parfaite, mais une conduite juste. »",
    'Avancer sobrement, même quand la journée résiste.',
    ["Garder uniquement l'essentiel", 'Avancer une étape après l’autre', 'Préserver sa maîtrise jusqu’au soir'],
  ),
  [DayMode.elevation]: mode(
    DayMode.elevation,
    'elevation',
    'elevation',
    '#E0C070',
    'ascendante et ouverte',
    'dense',
    'maximale',
    'Exigence. Élan. Maîtrise.',
    "« S'élever consiste à approfondir le geste, pas à multiplier les gestes. »",
    'S’élever sans se disperser.',
    ['Choisir le domaine à élever', 'Exécuter le geste exigeant', 'Mesurer le progrès avec humilité'],
  ),
  [DayMode.momentDecisif]: mode(
    DayMode.momentDecisif,
    'moment-decisif',
    'moment-decisif',
    '#EBD9A3',
    'concentrée vers un point',
    'resserrée',
    'maximale',
    'Présence. Précision. Décision.',
    '« Préparer ce qui dépend de soi, puis entrer pleinement dans le moment. »',
    "Tout ce qui n'est pas la décision peut attendre.",
    ["Clarifier l'intention", 'Préparer le geste décisif', 'Éliminer toute distraction inutile'],
  ),
  [DayMode.imprevu]: mode(
    DayMode.imprevu,
    'imprevu',
    'imprevu',
    '#A87A55',
    'changeante puis recentrée',
    'aérée',
    'minimale',
    'Absorber. Trier. Reprendre.',
    '« Accepter le changement de plan sans abandonner sa direction. »',
    "La journée a changé de forme ; garder l'essentiel.",
    ["Sécuriser l'essentiel", 'Recomposer la journée', 'Abandonner le non-indispensable'],
  ),
  [DayMode.transition]: mode(
    DayMode.transition,
    'transition',
    'transition',
    '#8A8170',
    "progressive de l'ombre vers la lumière",
    'standard',
    'basse',
    'Passage. Repère. Fondation.',
    "« Ne pas exiger immédiatement la maîtrise d'une vie qui vient de changer. »",
    'Entre deux régimes, préserver la continuité.',
    ['Accepter le nouveau rythme', 'Installer un premier repère', 'Poser une action fondatrice'],
  ),
} satisfies Record<DayMode, ModePresentationDefinition>;

export const MODE_PRESENTATION_LIST: readonly ModePresentationDefinition[] =
  Object.values(MODE_PRESENTATIONS);

export function getModePresentation(mode: DayMode): ModePresentationDefinition {
  return MODE_PRESENTATIONS[mode];
}
